import TagManager from 'react-gtm-module';
import * as IdbKeyVal from "idb-keyval";
import { format } from 'date-fns'
import axiosInstance from '../services/AxiosService';
import { appVersion as contextAppVersion } from '../context/AppVersionContext';
import * as GrindTables from './GrindTables';
import * as GolferHelper from './GolferHelper';
import * as FitterHelper from './FitterHelper';
import SimulatorConfiguration from './SimulatorHelper';
import * as ClubFittingHelper from './ClubFittingHelper';
import * as CountryListHelper from './CountryListHelper';
import * as GrindLookup from '../library/GrindLookup';
import * as Globals from './Globals';
import { WedgeOrder, ClubDetail, YourGame } from '../classes/WedgeOrder';
import ISimulator from '../classes/ISimulator';
import Fitter from '../classes/Fitter';
import Golfer from '../classes/Golfer';
import { Locale } from '../classes/Locale';
import { PipaConsentValue } from '../classes/PipaConsent';
import { ParaglidingOutlined } from '@mui/icons-material';

//ISO date-fns notes in case I need it in the future.  See solution near bottom.  https://github.com/date-fns/date-fns/discussions/2366#discussioncomment-532000

export class YourTechnique {
    constructor(public full: string, public pitch: string, public openFace: string) { }
}


//10/2023: Adding logging to better track and TypeError being triggered somewhere in this block of order properties
const OrderErrorKey = "wedgeOrderErrorKey";

export const createOrder = async (golfer: Golfer, fitter: Fitter, tmConfiguration: SimulatorConfiguration) => {
    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 1");

    //    let [golferFirstName = "", golferLastName = ""] = golfer.fullName.split(" ");
    let today = new Date();
    let utcOffsetMinutes = today.getTimezoneOffset() * -1;
    let utcOffset = `${Math.sign(utcOffsetMinutes) === 1 ? "" : "-"}${Math.round(Math.abs(utcOffsetMinutes / 60)).toString().padStart(2, "0")}:${Math.abs(utcOffsetMinutes % 60).toString().padStart(2, "0")}`;

    let appVersion = contextAppVersion || "VWF-Error";

    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 2");
    let order = new WedgeOrder();
    if (golfer.orderId) {
        order.objectId = golfer.orderId;
    } else {
        order.objectId = (window.crypto as any).randomUUID();
    }
    try {
        order.accountNumber = fitter.accountNumber;
    }
    catch { }
    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 3");
    order.appVersion = appVersion;
    order.appVersions = appVersion;
    order.appointmentID = 0;
    order.clientEmail = golfer.email;
    order.clientFirstName = golfer.firstName;
    order.clientLastName = golfer.lastName;
    order.clientPhoneNumber = addPhoneCountryCode(golfer.phone) || "";
    order.comments = golfer.fittingNotes;
    order.country = fitter.country;
    order.fitterLocale = fitter.locale;
    order.utcOffset = utcOffset;
    order.dateFitted = (golfer.fittingDate).toISOString();
    order.dateUploaded = (today.getTime() / 1000).toString();
    order.deviceID = tmConfiguration.deviceId;
    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 4");
    order.emailRecipients = new Array<string>();
    if (golfer.email != "") {
        order.emailRecipients.push(golfer.email);
    }
    order.smsOptIn = golfer.smsOptIn;
    order.emailRecipients.push(fitter.email);
    order.eventDate = format(golfer.fittingDate, "yyyy-MM-dd");
    order.eventType = "Vokey Wedge Fitting";
    order.regionID = Number(fitter.regionID);
    order.fitterID = fitter.fitterID;
    order.fitterEmail = fitter.email;

    if (golfer?.consentDataCollection && golfer.consentDataCollection !== "") {
        order.consentDataCollection = golfer.consentDataCollection as PipaConsentValue;    
    }
    if (golfer?.consentDataThirdparty && golfer.consentDataCollection !== "") {
        order.consentDataThirdparty = golfer.consentDataThirdparty as PipaConsentValue;
    }
    if (golfer?.consentDataInternational && golfer.consentDataCollection !== "") {
        order.consentDataInternational = golfer.consentDataInternational as PipaConsentValue;
    }

    let fitterFirstName = fitter.firstName || "";
    let fitterLastName = fitter.lastName || "";
    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 5");
    //no first/last name, so pull it from the name field
    if (fitterFirstName === "" && fitterLastName === "" && fitter.name !== "") {
        [fitterFirstName, fitterLastName] = fitter.name.split(" ");
    }
    order.fitterFirstName = fitterFirstName;
    order.fitterLastName = fitterLastName;
    order.locationID = fitter.locationID.toString();
    order.newsletterOptIn = "";
    order.status = "Complete";
    order.timestamp = golfer.timestamp.toISOString();
    order.venueEmail = "";
    order.venueName = fitter.locationName;
    order.venueType = "Vokey Wedge Fitting";
    order.venueTypeID = 14;

    //add all Greg's data to it
    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 6");
    order.headerJson = JSON.stringify(new YourGame(golfer.courseConditions, golfer.bunkerConditions, golfer.confidence));
    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 7");
    order.enhancedFittingData = "CDATA[" + JSON.stringify(ClubFittingHelper.getClubFittingPayload(golfer, fitter, tmConfiguration)) + "]";
    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 8");
    order.enhancedFittingData.replaceAll('\\"', '"').replaceAll('"data":"{', '"data":{').replaceAll('}]}}"},"shotRecommendation', '}]}}},"shotRecommendation');
    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 9");
    let loftsSelected = golfer.selectedLofts.reduce((a, b) => a + ' ' + b.loft, "").trim();
    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 10");
    let shotTypesArray = golfer.selectedLofts.map(fitting => {
        return fitting.loft + ' ' + fitting.shots.reduce((a, b) => a + (b.selected ? b.shotType + " " : ""), "").trim();
    })
    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 11");
    let shotTypes = shotTypesArray.join(', ');

    //get list of Why Grinds
    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 12");
    let whyGrindsArray = new Array<string>();
    golfer.selectedLofts.forEach(loft => {
        let grindRecommendation = golfer.grindRecommendations.filter(gr => gr.loft === loft.loft)[0];
        if (grindRecommendation !== undefined) {
            let matchPercentages = grindRecommendation.grindWeightings.map(gr => {
                let isSelected = grindRecommendation.selectedBounce === gr.bounce && grindRecommendation.selectedGrind === gr.grind ? " selected" : "";
                let match = `${grindRecommendation.loft}.${gr.bounce} ${gr.grind}, ${gr.weightPercent}% ${isSelected}`;
                return match;
            })
            whyGrindsArray.push(matchPercentages.join(", "));
            //        let shotDetail = new ShotDetail();
        }
    })
    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 13");
    let whyGrinds = whyGrindsArray.join(", ");


    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 14");
    var headModel = GrindTables.showSM11(fitter.showSm11) ? "SM11" : "SM9";
    let clubs = golfer.grindRecommendations.map(grind => {
        let set = `${grind.loft}.${grind.selectedBounce} ${grind.selectedGrind} Grind`;
        let hand = golfer.hand === "left" ? "LH" : "RH";
        let lineNotes = grind.notes.map(g => g.value).join("\\n");
        let matchPercentage = grind.grindWeightings.map(gr => `${grind.loft}.${gr.bounce} ${gr.grind} ${gr.weightPercent}%`).join(", ");
        let clubDetail = new ClubDetail(grind.lineNumber, "Vokey", "844S", 1, 1, hand, headModel, false, set, JSON.stringify({ lineNote: lineNotes, matchPercentage: matchPercentage }));
        return clubDetail;
    })

    await IdbKeyVal.set(OrderErrorKey, "createOrder() step 15");
    order.clubs = clubs;
    if (!Globals.isProductionEnvironment) console.log("payload", order)
    return order;
}

const getCountry = (country: string) => {
    var newCountry = country.toUpperCase();
    let countryList = CountryListHelper.list.map(item => { return { ...item, id: item.id.toUpperCase(), value: item.value.toUpperCase() } });
    //check if the country code matches the full country's name instead of the 2 digit code (this is due to older versions of the app using a textbox instead of dropdown)
    let countryLookup = countryList.find(item => item.value === newCountry);
    //in early versions, user might manually enter country abbreviation instead of country name. check for that
    if (countryLookup === undefined) {
        countryLookup = countryList.find(item => item.id === newCountry);
    }
    if (countryLookup) {
        newCountry = countryLookup.id;
    }
    newCountry = (newCountry === "" || countryLookup === undefined) ? "US" : newCountry;
    return newCountry;
}

const getLocale = (locale: string, country: string) => {
    var newLocale = locale;

    if (newLocale === "" || newLocale.indexOf("-") !== 2) {
        let localeLookup = CountryListHelper.list.find(i => i.id === country);
        if (localeLookup) {
            newLocale = localeLookup.locale;
        }
    }
    //per Bill, use this for now. He will discuss options with Chris later.  07/12/2022
    newLocale = newLocale || Locale.english;

    return newLocale;
}

//can't use existing regionId b/c older versions of app all used "1" for region. Must do the lookup to insure accuracy
const getRegion = (country: string) => {
    // Default to Region 1 US
    let newRegion = 1;

    let regionLookup = CountryListHelper.list.find(i => i.id === country);
    if (regionLookup) {
        newRegion = regionLookup.region;
    }
    return newRegion;
}

export async function placeOrder(jwtToken: string, wedgeOrder: WedgeOrder) {
    await IdbKeyVal.set(OrderErrorKey, "placeOrder() step 1");

    jwtToken = await FitterHelper.getFitterJwt();
    await IdbKeyVal.set(OrderErrorKey, "placeOrder() step 2");
    return new Promise((resolve, reject) => {
        axiosInstance.post(process.env.REACT_APP_SECURE_API_GATEWAY! + '/api/fittings', wedgeOrder)
            .then(response => {
                resolve(response.status);
            })
            .catch((error) => {
                console.error(new Date(), 'place order failed ', error.message);
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'log_error',
                        description: 'Wedge Order Helper - place order',
                        msg: 'catch: ' + error
                    }
                });
                reject(error);
            });
    })
}

//for testing only - reset order status so we can test orders without doing the actual fittings
export const resetGolferStatus = async () => {
    let golferList = await GolferHelper.getGolferListByFitter(0);
    let golfers = golferList.filter(g => g.orderStatus === 'sent');
    if (golfers.length > 0) {
        let golfer = golfers[0];
        golfer.orderStatus = 'queued';
        await GolferHelper.upsertGolfer(golfer);
        setTimeout(() => {
            resetGolferStatus();
        }, 1000);
    }
}

//save the golfer orders in a semi-recursive method to get around async not working with loops.
//pass in an Id and find the next un-sent record greater than that Id. When finished saving to the back-end, call method again with current id.
export const placeAllQueuedOrders = async (nextId: number, tmConfiguration: SimulatorConfiguration, simulator: ISimulator) => {
    let fitterList = await FitterHelper.getAllFitters();
    //get sorted list of fittings that haven't been sent yet
    let fittings = await getInQueueFittings();
    if (fitterList && fitterList.length && fittings && fittings.length > 0) {
        var isOffline = false;
        //get the next golfer record in the queue based on the previous value passed to this function
        fittings = fittings.filter(golfer => golfer.id >= nextId);
        if (fittings.length > 0) {
            let golfer = fittings[0];
            let fitter = {} as Fitter;
            //since we are tranistioning to using emails as a key, older records won't have them yet.
            if (golfer.fitterEmail !== undefined && golfer.fitterEmail !== "") {
                let fitters = fitterList.filter(f => f.email === golfer.fitterEmail);
                if (fitters.length > 0) {
                    fitter = fitters[0];
                }
            } else {
                fitter = fitterList.filter(f => f.fitterID === golfer.fitterId)[0];
            }
            var wedgeOrder = new WedgeOrder();
            try {
                wedgeOrder = await createOrder(golfer, fitter, tmConfiguration);
            }
            catch (error) {
                golfer.orderStatus = 'error';
                await GolferHelper.upsertGolfer(golfer);
                let orderError = await IdbKeyVal.get(OrderErrorKey);
                console.error(new Date(), 'all orders - order error', orderError, error);

            }
            if (wedgeOrder.objectId !== "") {
                try {
                    let awsIdToken = ""
                    if (fitter && fitter.awsIdToken){
                        awsIdToken = fitter.awsIdToken;
                    }
                    let status = await placeOrder(awsIdToken, wedgeOrder);
                    if (status === 200) {
                        //success. save order status to local db
                        Globals.logDev(`${new Date()}, 'ORDER SENT'`);
                        golfer.orderStatus = 'sent';
                        await GolferHelper.upsertGolfer(golfer);
                        //gtm event that order was sent from the queue
                        var yourTechnique = calculateYourTechnique(golfer, fitter, simulator, new YourTechnique("", "", ""));
                        let dataLayer = getGtmDataLayer('vwf_fitting_complete_email_sent', golfer, fitter, yourTechnique, true);
                        TagManager.dataLayer({
                            dataLayer: dataLayer
                        });
                    }
                }
                catch (err) {
                    TagManager.dataLayer({
                        dataLayer: {
                            event: 'log_error',
                            description: 'Wedge Order Helper - place all queued orders',
                            msg: 'catch: ' + err
                        }
                    });
                    // if off line, stop processing orders
                    if (Globals.errMsgIsOffline(err.message)) {
                        Globals.logDev(`${new Date()}, 'is offline'`);
                        isOffline = true;
                    }
                }
            }
            //process the next fitting in the queue
            if (!isOffline) {
                await placeAllQueuedOrders(golfer.id + 1, tmConfiguration, simulator);
            }
        }
    }
}

//get all fittings that are in the queue and haven't been sent yet
export const getInQueueFittings = async () => {
    let fitterList = await FitterHelper.getAllFitters();
    let golferList = await GolferHelper.getGolferListByFitter(0);

    if (fitterList && golferList && golferList.length > 0) {
        //in queue fittings must have the correct status as well as a matching fitter Id
        //note: g.orderStatus === undefined is only here for much older versions (e.g. mid 2022) of the app that may not have been updated since facilities aren't required to do so
        let fitterIds = fitterList.map(f => f.fitterID);
        golferList = golferList.filter(g => fitterIds?.includes(g.fitterId) && g.grindRecommendations.length > 0 && (g.orderStatus === undefined || g.orderStatus === 'queued')).sort((a, b) => a.id - b.id);
    }
    return golferList;
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const getLocationName = async (id: number, jwtToken: string): Promise<string> => {
    return new Promise((resolve, reject) => {
        axiosInstance.get(process.env.REACT_APP_SECURE_API_GATEWAY! + '/api/locations/partner/' + id)
            .then(response => {
                if (response.data) {
                    resolve(response.data.Name);
                } else {
                    resolve("");
                }
            })
            .catch((error) => {
                //when called for user pool 2, this always errors out b/c users in pool2 don't have locations
                console.error("Get location fail", error);
                resolve("");
            });
    })
}

export const getGtmDataLayer = (eventName: string, golfer: Golfer, fitter: Fitter, yourTechnique: YourTechnique, isQueue: boolean) => {
    var items = Array<any>();
    var headModel = GrindTables.showSM11(fitter.showSm11) ? "SM11" : "SM9";
    golfer.grindRecommendations.forEach(grind => {
        items.push({
            item_brand: 'Vokey',
            item_name: headModel,
            item_variant: `recommended - ${(grind.selectedBounce === grind.grindWeightings[0].bounce).toString()}`,
            quantity: 1,
            item_category: golfer.hand + " Hand",
            item_id: `${grind.loft}.${grind.selectedBounce} ${grind.selectedGrind} Grind`
        })
    })


    return {
        event: eventName,
        golfer_id: golfer.id,
        fitter_id: fitter.fitterID,
        fit_id: golfer.orderId,
        country: fitter.country,
        loc_name: fitter.locationName,
        golfer_course: golfer.courseConditions,
        golfer_bunker: golfer.bunkerConditions,
        golfer_confidence: golfer.confidence,
        golfer_technique_full: yourTechnique.full,
        golfer_technique_medium: yourTechnique.pitch,
        golfer_technique_open_face: yourTechnique.openFace,
        is_queue: isQueue,
        items: items
    }
}

//need to refactor this after the presentation. This code is copy/pasted across the app
export const calculateYourTechnique = (golfer: Golfer, fitter: Fitter, simulator: ISimulator, yourTechnique: YourTechnique) => {
    let newYourTechnique = { ...yourTechnique };
    let attackAngle = new GrindLookup.AttackAngleAverage(0, 0);
    let attackAngleAverages: GrindLookup.ShotTypeAttackAngleAverage = new Map([["Full", new GrindLookup.AttackAngleAverage(0, 0)], ["Pitch", new GrindLookup.AttackAngleAverage(0, 0)], ["Open Face", new GrindLookup.AttackAngleAverage(0, 0)]]);
    golfer.loftsForFitting.forEach(loft => {
        loft.practiceShots.forEach(shot => {
            if (shot.status === "closed" && shot.data) {
                let aa_sl = simulator.calculateAttackAngleShaftLean(loft.loft, loft.shotType, shot.data!.attackAngle, shot.data!.dynamicLoft, true, true, fitter);
                if (aa_sl.attackAngle != "--") {
                    //update the attack angle object for the current shot type
                    attackAngle = attackAngleAverages.get(loft.shotType)!;
                    attackAngle.numberShots++;
                    attackAngle.totalAttackAngle += Number(aa_sl.attackAngle);
                }
            }
        })
        switch (loft.shotType) {
            case "Full":
                if (attackAngle.numberShots > 0) {
                    newYourTechnique.full = GrindLookup.calculateDivotType(attackAngle.totalAttackAngle / attackAngle.numberShots);
                }
                break;
            case "Pitch":
                if (attackAngle.numberShots > 0) {
                    newYourTechnique.pitch = GrindLookup.calculateDivotType(attackAngle.totalAttackAngle / attackAngle.numberShots);
                }
                break;
            case "Open Face":
                if (attackAngle.numberShots > 0) {
                    newYourTechnique.openFace = GrindLookup.calculateDivotType(attackAngle.totalAttackAngle / attackAngle.numberShots);
                }
                break;
        }
    });

    return newYourTechnique
}

const addPhoneCountryCode = (phoneNumber: string | undefined) => {
    if (!phoneNumber) return phoneNumber;
    
    return phoneNumber.startsWith("+1")
        ? phoneNumber
        : "+1" + phoneNumber;
}
